import React, { useEffect, useRef, useState } from 'react';

type CountUpProps = {
  to: number;
  durationMs?: number;
  className?: string;
};

/**
 * Animates a number from 0 to `to` once it scrolls into view.
 * Respects prefers-reduced-motion by jumping straight to the final value.
 */
const CountUp: React.FC<CountUpProps> = ({ to, durationMs = 900, className }) => {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setValue(to);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;

    const run = () => {
      if (started.current) return;
      started.current = true;
      let startTs: number | null = null;
      const step = (ts: number) => {
        if (startTs === null) startTs = ts;
        const progress = Math.min((ts - startTs) / durationMs, 1);
        setValue(Math.round(progress * to));
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) run();
      });
    }, { threshold: 0.4 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [to, durationMs]);

  return <span ref={ref} className={className}>{value}</span>;
};

export default CountUp;
